import { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import RedeemIcon from '@mui/icons-material/Redeem';
import Loading from 'components/system/Loading';
import Paper from 'components/system/Paper';
import Icon from 'components/system/Icon';
import { useModalContext } from 'components/system/ModalContext';
import withAttachCardDialog from 'components/hoc/withAttachCardDialog';
import usePaymentsStore from 'store/paymentStore';
import colors from 'styles/colors';
import { useGetBalance } from 'hooks/useBillingAccount';
import AddBalanceForm from './AddBalanceForm';
import RedeemCouponForm from './RedeemCouponForm';

const Balance = ({ openAttachCard }) => {
  const [isAddBalanceClicked, setIsAddBalanceClicked] = useState(false);
  const {
    total,
    credit,
    hasPromo,
    promoExpirationDate,
    isLoading,
  } = usePaymentsStore((state) => state.balance);
  const { card } = usePaymentsStore((state) => state.paymentMethod);
  const { refetch } = useGetBalance();
  const { openModal, closeModal, setModalContent } = useModalContext();

  const handleUpdateBalance = () => {
    refetch();
    closeModal();
  };

  const resetAddBalanceState = () => {
    setIsAddBalanceClicked(false);
  };

  const openAddBalanceModal = () => {
    openModal({
      content: <AddBalanceForm onBalanceUpdate={handleUpdateBalance} />,
      onCloseCallback: resetAddBalanceState,
    });
  };

  const openRedeemCouponModal = () => {
    openModal({
      content: <RedeemCouponForm onRedeemUpdate={handleUpdateBalance} />,
      onCloseCallback: resetAddBalanceState,
    });
  };

  const handleAddBalanceClick = () => {
    setIsAddBalanceClicked(true);
    if (card) {
      openAddBalanceModal();
    } else {
      openAttachCard();
    }
  };

  useEffect(() => {
    if (card && isAddBalanceClicked) {
      setModalContent(
        <AddBalanceForm
          onCloseCallback={resetAddBalanceState}
          onBalanceUpdate={handleUpdateBalance}
        />,
      );
    }
  }, [!!card]);

  return (
    <Paper
      sx={{
        background: 'radial-gradient(72.31% 330.56% at 71.51% -102.31%, #0060FA 0%, #CC74F5 100%)',
        height: '168px',
        padding: '24px 0 0',
        border: 'none',
      }}
      className={hasPromo ? 'balanceContainer' : ''}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-around"
        alignItems="center"
        sx={{ height: '100%' }}
      >
        {
          isLoading ? (
            <Grid item>
              <Loading />
            </Grid>
          ) : (
            <>
              <Grid
                item
                xs={9}
                sx={{
                  paddingLeft: '12px', display: 'flex', alignItems: 'center', minHeight: '72px',
                }}
              >
                <Box sx={{ width: '44px', height: '44px', marginRight: '15px' }}>
                  <Box sx={{
                    padding: '9px 12px 12px 12px',
                    borderRadius: '30px',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    background: 'radial-gradient(100% 100% at 50% 0%, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.1) 100%)',
                  }}
                  >
                    <Icon name="credit_card" color={colors.text.primary} size="18px" />
                  </Box>
                </Box>
                <Box>
                  <Typography
                    className="balanceTitle"
                    variant="buttonLarge"
                    sx={{ display: 'block', color: colors.text.primary, marginTop: '4px' }}
                  >
                    Total Balance
                  </Typography>
                  { hasPromo && (
                    <Typography
                      variant="body2"
                      sx={{ display: 'block', color: colors.text.primary, marginLeft: '20px' }}
                    >
                      Credits (expiring on
                      {' '}
                      {promoExpirationDate}
                      )
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={3} sx={{ textAlign: 'right', paddingRight: '12px' }}>
                <Typography variant="h5" sx={{ display: 'block', color: colors.text.primary }}>
                  $
                  {total}
                </Typography>
                { hasPromo && (
                  <Typography variant="buttonLarge" sx={{ display: 'block', color: colors.text.primary }}>
                    $
                    {credit}
                  </Typography>
                )}
              </Grid>
              <Grid className="balanceButtonContainer" item xs={12} sx={{ marginTop: '34px' }}>
                <div style={{ display: 'flex' }}>
                  <Button
                    sx={{
                      width: '100%',
                      height: '60px',
                      borderTopRightRadius: 0,
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: '24px',
                      borderBottomRightRadius: '24px',
                      border: 0,
                      backdropFilter: 'blur(60px)',
                      '&:hover, &:active': {
                        border: 0,
                      },
                      color: colors.text.primary,
                    }}
                    variant="outlined"
                    onClick={handleAddBalanceClick}
                  >
                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ padding: '0 12px' }}
                    >
                      <Typography variant="body3" color={colors.text.primary}>
                        Add Balance
                      </Typography>
                      <Icon name="plus" color={colors.text.primary} size="18px" />
                    </Grid>
                  </Button>
                  <Button
                    sx={{
                      width: '100%',
                      height: '60px',
                      borderTopRightRadius: 0,
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: '24px',
                      borderBottomRightRadius: '24px',
                      border: 0,
                      backdropFilter: 'blur(60px)',
                      '&:hover, &:active': {
                        border: 0,
                      },
                      color: colors.text.primary,
                    }}
                    variant="outlined"
                    onClick={openRedeemCouponModal}
                  >
                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ padding: '0 12px' }}
                    >
                      <Typography variant="body3" color={colors.text.primary}>
                        Redeem Coupon
                      </Typography>
                      <RedeemIcon color={colors.text.primary} size="18px" />
                    </Grid>
                  </Button>
                </div>
              </Grid>
            </>
          )
        }
      </Grid>
    </Paper>
  );
};

export default withAttachCardDialog(Balance, { isClosable: false });
